import {
  ArrowRightOutlined,
  FolderOutlined,
  UploadOutlined
} from "@ant-design/icons"
import { Button, Flex, List, Typography, Upload } from "antd"
import type { UploadFile } from "antd/es/upload/interface"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { FilePreviewModal } from "../../../../components/FilePreviewModal/FilePreviewModal.tsx"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { type LeadAttachment } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { WorkspaceCardValue } from "../cards/WorkspaceCardValue.tsx"
import { fileIconFor } from "../lead-attachment-icon.tsx"
import {
  LEAD_UPLOAD_ACCEPT,
  attachmentDate,
  attachmentName,
  attachmentType,
  leadUploadPatch,
  validateLeadUpload,
  visibleLeadAttachments
} from "../lead-attachments.ts"
import { type LeadPatchSender } from "../lead-workspace-patch.ts"
import { OverviewCardTitle } from "./OverviewCardIcon.tsx"

const { Text } = Typography

/** A quick-glance summary only — full search/filter/manage stays on the
 * Files tab. Showing more than a handful here would just duplicate it. */
const RECENT_FILES_LIMIT = 5

interface LeadOverviewFilesCardProps {
  attachments?: LeadAttachment[] | null
  canEdit?: boolean
  saving?: boolean
  onPatch: LeadPatchSender
  onViewAll: () => void
}

function LeadOverviewFilesCard({
  attachments,
  canEdit,
  saving,
  onPatch,
  onViewAll
}: LeadOverviewFilesCardProps) {
  const { t } = useTranslation()
  const { parseDate } = useDateFormat()
  const [preview, setPreview] = useState<LeadAttachment | null>(null)

  const files = visibleLeadAttachments(attachments)

  // Newest first — the same date field and parser the Files tab's own Date
  // filter already relies on, since `created` arrives pre-formatted, not ISO.
  const sorted = useMemo(() => {
    const withDate = (file: LeadAttachment) =>
      parseDate(attachmentDate(file))?.valueOf() ?? 0

    return [...files].sort((a, b) => withDate(b) - withDate(a))
  }, [files, parseDate])

  const recent = sorted.slice(0, RECENT_FILES_LIMIT)

  const upload = (changed: UploadFile, fileList: UploadFile[]) => {
    const patch = leadUploadPatch(changed, fileList)
    if (patch) void onPatch(patch)
  }

  const uploadButton = canEdit && (
    <Upload
      multiple
      showUploadList={false}
      accept={LEAD_UPLOAD_ACCEPT}
      beforeUpload={validateLeadUpload}
      onChange={({ file, fileList }) => upload(file, fileList)}
    >
      <Button
        size={"small"}
        icon={<UploadOutlined />}
        loading={saving}
        aria-label={t("leadWorkspace.cards.attachments.upload")}
      >
        {t("leadWorkspace.cards.attachments.upload")}
      </Button>
    </Upload>
  )

  return (
    <WorkspaceCard
      cardId={"files"}
      title={
        <OverviewCardTitle icon={<FolderOutlined />} accent={"indigo"}>
          {t("leadWorkspace.cards.attachments.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.cards.attachments.title")}
      className={"h-full"}
      extra={
        <Flex gap={"small"} align={"center"}>
          {!!files.length && (
            <Button
              size={"small"}
              type={"link"}
              icon={<ArrowRightOutlined />}
              onClick={onViewAll}
            >
              {t("leadWorkspace.cards.attachments.viewAll")}
            </Button>
          )}
          {uploadButton}
        </Flex>
      }
    >
      <WorkspaceCardValue
        value={files.length || null}
        placeholder={t("leadWorkspace.cards.attachments.emptyShort")}
      >
        <Flex vertical gap={"small"}>
          <List
            size={"small"}
            dataSource={recent}
            renderItem={(file) => (
              <List.Item key={file.id} className={"!border-b-0 !py-1.5"}>
                <Flex
                  gap={8}
                  align={"center"}
                  justify={"space-between"}
                  className={"w-full min-w-0"}
                >
                  <Flex gap={8} align={"center"} className={"min-w-0"}>
                    <span className={"shrink-0 text-gray-400"}>
                      {fileIconFor(file)}
                    </span>
                    <Text
                      ellipsis={{ tooltip: attachmentName(file) }}
                      className={"cursor-pointer"}
                      onClick={() => setPreview(file)}
                    >
                      {attachmentName(file)}
                    </Text>
                  </Flex>
                  <Flex gap={16} align={"center"} className={"shrink-0"}>
                    <Text type={"secondary"} className={"text-xs"}>
                      {attachmentType(file)}
                    </Text>
                    <Text
                      type={"secondary"}
                      className={"w-28 text-right text-xs"}
                    >
                      {attachmentDate(file)}
                    </Text>
                  </Flex>
                </Flex>
              </List.Item>
            )}
          />
          <Text type={"secondary"} className={"text-center text-xs"}>
            {t("leadWorkspace.cards.attachments.recentCount", {
              shown: recent.length,
              total: files.length
            })}
          </Text>
        </Flex>
      </WorkspaceCardValue>

      <FilePreviewModal
        visible={!!preview}
        onClose={() => setPreview(null)}
        doc={preview}
      />
    </WorkspaceCard>
  )
}

export { LeadOverviewFilesCard }
